import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CalendarDays, Clock, UserRound, MapPin, Info } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { api } from '../services/api';
import { V2, contactLink } from '../v2/shared';
import { clinicTime, clinicDateKey, dateFromKey } from '../utils/clinicTime';

interface View {
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  startTime: string; endTime: string; patientFirstName: string;
  professionalName: string | null; specialtyName: string | null; roomName: string | null;
  canCancel: boolean; cancelMinHours: number;
}

const COPY: Record<View['status'], { chip: string; cls: string; text: string }> = {
  pending: { chip: 'Esperando confirmación', cls: 'chip-wait', text: 'Tu horario está reservado. Lo revisamos y te confirmamos por email en menos de 24 horas hábiles.' },
  confirmed: { chip: 'Confirmado', cls: 'chip-ok', text: 'Tu turno está confirmado. Te vamos a enviar un recordatorio el día anterior.' },
  cancelled: { chip: 'Cancelado', cls: 'chip-off', text: 'Este turno fue cancelado y el horario quedó liberado.' },
  completed: { chip: 'Realizado', cls: 'chip-done', text: 'Este turno ya se realizó.' },
};
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const ReservationStatus: React.FC = () => {
  const { token = '' } = useParams();
  const [view, setView] = useState<View | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'missing' | 'error'>('loading');
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/public/reservations/${token}`);
      setView(r.data); setState('ok');
    } catch (e: any) {
      setState(e.response?.status === 404 ? 'missing' : 'error');
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const cancel = async () => {
    setBusy(true); setErr('');
    try {
      await api.post(`/public/reservations/${token}/cancel`);
      setAsking(false);
      await load();
    } catch (e: any) {
      setErr(e.response?.data?.error || 'No pudimos cancelar. Probá de nuevo en unos minutos.');
      await load();
    } finally { setBusy(false); }
  };

  const contact = (
    <a href={contactLink.href} {...(contactLink.external ? { target: '_blank', rel: 'noreferrer' } : {})} style={{ fontWeight: 700, textDecoration: 'underline' }}>
      escribinos por {contactLink.label}
    </a>
  );

  return (
    <V2>
      <main className="auth">
        <div className="auth-card" style={{ maxWidth: 520 }}>
          {state === 'loading' && <div className="skel" style={{ height: 280 }} />}

          {(state === 'missing' || state === 'error') && (
            <div className="panel" style={{ textAlign: 'center' }}>
              <h1 style={{ fontSize: 26 }}>{state === 'missing' ? 'No encontramos esta reserva' : 'No pudimos cargar tu reserva'}</h1>
              <p className="hint" style={{ margin: '8px 0 18px' }}>
                {state === 'missing' ? 'Revisá que hayas abierto el enlace completo del email.' : 'Probá de nuevo en unos minutos.'}
              </p>
              <Link to="/" className="btn2 btn2-solid">Ir al inicio</Link>
            </div>
          )}

          {state === 'ok' && view && (<>
            <div className="head">
              <h1>Hola, {view.patientFirstName}</h1>
              <p>Estado de tu reserva</p>
            </div>
            <article className="panel">
              <div className="appt-top" style={{ marginBottom: 12 }}>
                <h3 style={{ fontSize: 20 }}>{view.specialtyName || 'Consulta'}</h3>
                <span className={`chip ${COPY[view.status].cls}`}>{COPY[view.status].chip}</span>
              </div>
              <p className="appt-line"><CalendarDays size={16} /> {cap(format(dateFromKey(clinicDateKey(view.startTime)), "EEEE d 'de' MMMM", { locale: es }))}</p>
              <p className="appt-line"><Clock size={16} /> {clinicTime(view.startTime)} hs</p>
              <p className="appt-line"><UserRound size={16} /> {view.professionalName || 'Profesional a confirmar'}</p>
              {view.roomName && <p className="appt-line"><MapPin size={16} /> {view.roomName}</p>}

              <div className="alert info" style={{ marginTop: 16, marginBottom: 0 }}>
                <Info size={18} style={{ flexShrink: 0 }} /><span>{COPY[view.status].text}</span>
              </div>

              {err && <p className="err" role="alert" style={{ marginTop: 12 }}>{err}</p>}

              {view.canCancel && !asking && (
                <button className="btn2 btn2-ghost" style={{ width: '100%', marginTop: 16 }} onClick={() => setAsking(true)}>Cancelar esta reserva</button>
              )}
              {view.canCancel && asking && (
                <div style={{ marginTop: 16 }} role="alertdialog" aria-label="Confirmar cancelación">
                  <p style={{ fontWeight: 600, marginBottom: 10 }}>¿Seguro que querés cancelarla? El horario se libera para otras personas.</p>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button className="btn2 btn2-solid" disabled={busy} onClick={cancel}>{busy ? 'Cancelando…' : 'Sí, cancelar'}</button>
                    <button className="btn2 btn2-ghost" disabled={busy} onClick={() => setAsking(false)}>No, mantenerla</button>
                  </div>
                </div>
              )}
              {!view.canCancel && (view.status === 'pending' || view.status === 'confirmed') && (
                <p className="hint" style={{ marginTop: 14, fontSize: 13.5 }}>
                  Para cancelar o cambiar un turno con menos de {view.cancelMinHours} horas de anticipación, {contact}.
                </p>
              )}
            </article>
            <p className="auth-alt"><Link to="/#especialidades">Pedir otro turno</Link></p>
          </>)}
        </div>
      </main>
    </V2>
  );
};

export default ReservationStatus;
